/*
 * Copyright (c) 2021 TechnoL0g.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 */

import Chain from '@/services/chain';
import {xbtsAssets, xbtsFiat, defiAssets} from '@/config';
import Explorer from '@/services/explorer';

export default {
    namespaced: true,

    state: {
        assets: {}, // key = id
        assetsFiat: {}, // key = id
        assetsLp: {}, // key = id
        holders: {},
    },

    getters: {
        assets: state => state.assets,
        assetsFiat: state => state.assetsFiat,
        assetsLp: state => state.assetsLp,
        holders: state => state.holders,
    },

    mutations: {
        SET_RESET(state) {
            state.assets = {};
            state.holders = {};
        },
        SET_ASSET(state, payload) {
            payload.timestamp = Math.floor(Date.now() / 1000);
            state.assets[payload.symbol] = payload;
        },
        SET_HOLDERS(state, payload) {
            state.holders[payload.symbol] = payload.holders;
        },
        SET_ASSETS(state, payload) {
            state.assets = payload;
        },
        SET_ASSETS_FIAT(state, payload) {
            state.assetsFiat = payload;
        },
        SET_ASSETS_LP(state, payload) {
            state.assetsLp = payload;
        },
        SET_ASSET_LP(state, payload) {
            state.assetsLp[payload.share.symbol] = payload;
        },
    },

    actions: {
        reset({commit}) {
            commit('SET_RESET')
        },
        async getAsset({commit}, symbol) {
            //return await Chain.getAsset(symbol);
            return await Explorer.getAsset(symbol);
        },
        async setAsset({commit}, values) {
            if (values) {
                commit('SET_ASSET', values);
            }
            return values;
        },
        async getHolders({commit}, asset) {
            const holders = await Explorer.holders(asset);
            commit('SET_HOLDERS', holders);
            return holders;
        },
        async getAssetsByIds({commit}, ids = []) {
            return await Explorer.getObjects(ids);
        },
        async getAssets({commit}, symbols = []) {
            //const data = await Chain.getAssets(symbols);
            const data = await Explorer.getAssets(symbols); // RPC

            if (data) {
                let result = {};
                for (let i = 0; i < data.length; i++) {
                    result[data[i].id] = data[i];
                }
                commit('SET_ASSETS', result);
            }
        },

        async getPoolAssets({commit}, poolId) {
            let pool = (await this.dispatch('app/getObjects', [poolId]))[0];
            const apyId = poolId.replace('1.19', '5.6');
            const poolAssets = (await this.dispatch('app/getObjects', [pool.asset_a, pool.asset_b, apyId]));
            pool.apy = poolAssets[2];
            const balanceA = (pool.balance_a / 10 ** poolAssets[0].precision).toFixed(poolAssets[0].precision) * 1;
            const balanceB = (pool.balance_b / 10 ** poolAssets[1].precision).toFixed(poolAssets[1].precision) * 1;

            pool.apy['_24h_exchange_fee_a'] = pool.apy['_24h_exchange_fee_a'] / 10 ** poolAssets[0].precision;
            pool.apy['_24h_withdrawal_fee_a'] = pool.apy['_24h_withdrawal_fee_a'] / 10 ** poolAssets[0].precision;
            pool.apy['total_withdrawal_fee_a'] = pool.apy['total_withdrawal_fee_a'] / 10 ** poolAssets[0].precision;
            pool.apy['total_exchange_fee_a'] = pool.apy['total_exchange_fee_a'] / 10 ** poolAssets[0].precision;


            pool.apy['_24h_exchange_fee_b'] = pool.apy['_24h_exchange_fee_b'] / 10 ** poolAssets[1].precision;
            pool.apy['_24h_withdrawal_fee_b'] = pool.apy['_24h_withdrawal_fee_b'] / 10 ** poolAssets[1].precision;
            pool.apy['total_withdrawal_fee_b'] = pool.apy['total_withdrawal_fee_b'] / 10 ** poolAssets[1].precision;
            pool.apy['total_exchange_fee_b'] = pool.apy['total_exchange_fee_b'] / 10 ** poolAssets[1].precision;

            const apyFeesExchangePercent = (((pool.apy['_24h_exchange_fee_a'] / balanceA * 100 * 365) + (pool.apy['_24h_exchange_fee_b'] / balanceB * 100 * 365)) / 2);
            const apyFeesWithdrawalPercent = (((pool.apy['_24h_withdrawal_fee_a'] / balanceA * 100 * 365) + (pool.apy['_24h_withdrawal_fee_b'] / balanceB * 100 * 365)) / 2);

            pool.apy.yr = (apyFeesExchangePercent + apyFeesWithdrawalPercent).toFixed(2);

            const btsPrice = this.getters['xbts/featuredPrice']['bts'];
            const tickers = this.getters['paprika/tickers'];
            let amountA  = {
                rub: 0,
                usd: 0,
                cny: 0,
            };

            if (tickers['BTS_' + poolAssets[0].symbol.replace('XBTSX.', '')]) {
                amountA['rub'] = (tickers['BTS_' + poolAssets[0].symbol.replace('XBTSX.', '')].last * btsPrice['rub'].price) * balanceA;
                amountA['usd'] = (tickers['BTS_' + poolAssets[0].symbol.replace('XBTSX.', '')].last * btsPrice['usd'].price) * balanceA;
                amountA['cny'] = (tickers['BTS_' + poolAssets[0].symbol.replace('XBTSX.', '')].last * btsPrice['cny'].price) * balanceA;
            }

            if (poolAssets[0].symbol === 'BTS') {
                amountA['rub'] = balanceA * btsPrice['rub'].price;
                amountA['usd'] = balanceA * btsPrice['usd'].price;
                amountA['cny'] = balanceA * btsPrice['cny'].price;
            }

            let amountB  = {
                rub: 0,
                usd: 0,
                cny: 0,
            };

            if (tickers['BTS_' + poolAssets[1].symbol.replace('XBTSX.', '')]) {
                amountB['rub'] = (tickers['BTS_' + poolAssets[1].symbol.replace('XBTSX.', '')].last * btsPrice['rub'].price) * balanceB;
                amountB['usd'] = (tickers['BTS_' + poolAssets[1].symbol.replace('XBTSX.', '')].last * btsPrice['usd'].price) * balanceB;
                amountB['cny'] = (tickers['BTS_' + poolAssets[1].symbol.replace('XBTSX.', '')].last * btsPrice['cny'].price) * balanceB;
            }

            pool.amountTotal = {
                rub: amountA['rub'] + amountB['rub'],
                usd: amountA['usd'] + amountB['usd'],
                cny: amountA['cny'] + amountB['cny'],
            };

            pool.A = {
                id: poolAssets[0].id,
                symbol: poolAssets[0].symbol,
                precision: poolAssets[0].precision,
                issuer: poolAssets[0].issuer,
                balance: balanceA,
                amountA: amountA,
                bitAsset: poolAssets[0].bitAsset,
                price: {
                    amount: (balanceB / balanceA).toFixed(poolAssets[1].precision) || 0,
                    symbol: poolAssets[1].symbol
                },
            };

            pool.B = {
                id: poolAssets[1].id,
                symbol: poolAssets[1].symbol,
                precision: poolAssets[1].precision,
                issuer: poolAssets[1].issuer,
                balance: balanceB,
                amountB: amountB,
                bitAsset: poolAssets[1].bitAsset,
                price: {
                    amount: (balanceA / balanceB).toFixed(poolAssets[0].precision) || 0,
                    symbol: poolAssets[0].symbol
                },
            };

            //console.log('pool', pool)
            return pool;
        },
        async getXbtsAssets({commit}) {
            let data = await Chain.getAssets(xbtsAssets);

            if (!data) {
                data = await Explorer.getAssets(xbtsAssets); // RPC
            }

            if (data) {
                let result = {};
                for (let i = 0; i < data.length; i++) {
                    result[data[i].symbol] = data[i];
                }
                commit('SET_ASSETS', result);
            }
            const dataFiat = await Chain.getAssets(xbtsFiat); // загрузка 14.445 sec
            //const dataFiat = await Explorer.getAssets(xbtsFiat); // RPC // RPC 15 sec
            if (dataFiat) {
                let result = {};
                for (let i = 0; i < dataFiat.length; i++) {
                    result[dataFiat[i].symbol] = dataFiat[i];
                }
                commit('SET_ASSETS_FIAT', result);
            }
        },
        async getLp({commit}, LpToken) {
            const dataLp = await Explorer.getAssets([LpToken]); // RPC
            let result = {};
            result = {
                share: dataLp[0],
                pool: await this.dispatch('assets/getPoolAssets', dataLp[0]['for_liquidity_pool']),
                supply: 0, //(await this.dispatch('app/getObjects', [dataLp[0].dynamic_asset_data_id]))[0]['current_supply'] / 10 ** dataLp[0].precision,
            }
            commit('SET_ASSET_LP', result);
            return result;
        },
    }
}
